/**
 * Tank select screen input — grid navigation, P1/P2 controls, AI toggle.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { isSoloMode } from '../mode';
import { justPressed, msToFrames } from '../input-edge';
import { getAllTankIds, getTankShot, getEffectiveShots, isTankDisabled, getVisibleTankIds, getShowBonusTanks, setShowBonusTanks, getGridDimensions } from '../../tanks';
import { tankColor, pickRandomThemeId, makePreviewSeed } from './helpers';
import { playSound } from '../../audio';
import { isTankUnlocked, purchaseTank } from '../../progression/storage';

/** Navigate a cursor index on the grid with wrapping. Skips empty and disabled cells. */
function gridMove(idx: number, dx: number, dy: number, visibleTankIds: string[]): number {
  const tankCount = visibleTankIds.length;
  const { cols, rows } = getGridDimensions(tankCount);
  const total = cols * rows;
  let col = idx % cols;
  let row = Math.floor(idx / cols);
  col = (col + dx + cols) % cols;
  row = (row + dy + rows) % rows;
  let newIdx = row * cols + col;

  // Try up to total cells to find a valid (non-empty, non-disabled) cell
  for (let attempt = 0; attempt < total; attempt++) {
    if (newIdx < tankCount && !isTankDisabled(visibleTankIds[newIdx])) return newIdx;
    // Advance in the movement direction to skip past disabled/empty
    if (dx !== 0) {
      col = (col + (dx > 0 ? 1 : -1) + cols) % cols;
    } else {
      row = (row + (dy > 0 ? 1 : -1) + rows) % rows;
    }
    newIdx = row * cols + col;
  }
  return idx; // fallback: stay put
}

export function handleTankSelectInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  const isOnline = !!state.multiplayer?.enabled;
  let tankIds = getVisibleTankIds(isOnline);

  if (isOnline) {
    // ONLINE: local player navigates grid + confirms
    const mySlot = state.multiplayer!.mySlot;
    const p = state.players[mySlot];
    if (!state.tankSelectReady[mySlot]) {
      // Bonus tanks toggle (= key)
      if (justPressed('Equal', input)) {
        setShowBonusTanks(!getShowBonusTanks());
        tankIds = getVisibleTankIds(true);
        if (!tankIds.includes(p.tankId)) {
          // Current tank is now hidden — snap to first core tank
          state.tankSelectCursor[mySlot] = 0;
          p.tankId = tankIds[0];
          p.color = tankColor(p.tankId);
        } else {
          // Remap cursor index to new list position
          const newIdx = tankIds.indexOf(p.tankId);
          if (newIdx !== -1) state.tankSelectCursor[mySlot] = newIdx;
        }
      }

      let moved = false;
      if (justPressed(Keys.A, input) || justPressed(Keys.ArrowLeft, input)) {
        state.tankSelectCursor[mySlot] = gridMove(state.tankSelectCursor[mySlot], -1, 0, tankIds); moved = true;
      }
      if (justPressed(Keys.D, input) || justPressed(Keys.ArrowRight, input)) {
        state.tankSelectCursor[mySlot] = gridMove(state.tankSelectCursor[mySlot], 1, 0, tankIds); moved = true;
      }
      if (justPressed(Keys.W, input) || justPressed(Keys.ArrowUp, input)) {
        state.tankSelectCursor[mySlot] = gridMove(state.tankSelectCursor[mySlot], 0, -1, tankIds); moved = true;
      }
      if (justPressed(Keys.S, input) || justPressed(Keys.ArrowDown, input)) {
        state.tankSelectCursor[mySlot] = gridMove(state.tankSelectCursor[mySlot], 0, 1, tankIds); moved = true;
      }
      if (moved) {
        playSound('menu_nav');
        const ci = state.tankSelectCursor[mySlot];
        p.tankId = ci < tankIds.length ? tankIds[ci] : tankIds[0];
        p.color = tankColor(p.tankId);
        // Default terraformerMode when hovering Terraformer
        if (p.tankId === 'terraformer' && !p.terraformerMode) p.terraformerMode = 'sapper';
      }
      // Terraformer mode toggle (Tab)
      if (p.tankId === 'terraformer' && justPressed(Keys.Tab, input)) {
        p.terraformerMode = p.terraformerMode === 'mason' ? 'sapper' : 'mason';
      }
      // Confirm (block if hovering disabled tank)
      if ((justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) && !isTankDisabled(p.tankId)) {
        if (!isTankUnlocked(p.tankId)) {
          // Attempt purchase
          if (purchaseTank(p.tankId)) playSound('menu_select');
        } else {
          playSound('menu_select');
          state.tankSelectReady[mySlot] = true;
          fx.push({ type: 'tank_select_confirmed', playerIndex: mySlot, tankId: p.tankId });
        }
      }
    }
  } else {
    // LOCAL: P1 = A/D/W/S + Space, P2 = Arrows + Enter
    const p1 = state.players[0];
    const p2 = state.players[1];

    // P1 navigation (A/D/W/S — solo modes also accept Arrows + Enter)
    if (!state.tankSelectReady[0]) {
      const solo = state.modeState ? isSoloMode(state.modeState.mode) : false;
      let moved = false;
      if (justPressed(Keys.A, input) || (solo && justPressed(Keys.ArrowLeft, input))) { state.tankSelectCursor[0] = gridMove(state.tankSelectCursor[0], -1, 0, tankIds); moved = true; }
      if (justPressed(Keys.D, input) || (solo && justPressed(Keys.ArrowRight, input))) { state.tankSelectCursor[0] = gridMove(state.tankSelectCursor[0], 1, 0, tankIds); moved = true; }
      if (justPressed(Keys.W, input) || (solo && justPressed(Keys.ArrowUp, input))) { state.tankSelectCursor[0] = gridMove(state.tankSelectCursor[0], 0, -1, tankIds); moved = true; }
      if (justPressed(Keys.S, input) || (solo && justPressed(Keys.ArrowDown, input))) { state.tankSelectCursor[0] = gridMove(state.tankSelectCursor[0], 0, 1, tankIds); moved = true; }
      if (moved) {
        playSound('menu_nav');
        const ci = state.tankSelectCursor[0];
        p1.tankId = ci < tankIds.length ? tankIds[ci] : tankIds[0];
        p1.color = tankColor(p1.tankId);
        if (p1.tankId === 'terraformer' && !p1.terraformerMode) p1.terraformerMode = 'sapper';
      }
      // P1 Terraformer mode toggle (Tab)
      if (p1.tankId === 'terraformer' && justPressed(Keys.Tab, input)) {
        p1.terraformerMode = p1.terraformerMode === 'mason' ? 'sapper' : 'mason';
      }
      if ((justPressed(Keys.Space, input) || (solo && justPressed(Keys.Enter, input))) && !isTankDisabled(p1.tankId)) {
        if (!isTankUnlocked(p1.tankId)) {
          if (purchaseTank(p1.tankId)) playSound('menu_select');
        } else {
          playSound('menu_select');
          state.tankSelectReady[0] = true;
        }
      }
    }

    // P2 AI toggle (Q = human/AI, E = cycle difficulty) — only in non-solo 2P modes
    const isSolo = state.modeState ? isSoloMode(state.modeState.mode) : false;
    if (!isSolo && !state.tankSelectReady[1]) {
      // Q = toggle P2 human/AI
      if (justPressed(Keys.Q, input)) {
        state.players[1].isAI = !state.players[1].isAI;
        if (state.players[1].isAI && state.players[1].aiDifficulty === 0) state.players[1].aiDifficulty = 5;
        state.players[1].name = state.players[1].isAI ? 'CPU' : 'P2';
      }
      // E = cycle P2 AI difficulty 1-9
      if (justPressed(Keys.E, input) && state.players[1].isAI) {
        state.players[1].aiDifficulty = (state.players[1].aiDifficulty % 9) + 1;
      }
    }

    // P2 navigation (Arrows) — skip in solo modes (only 1 player)
    if (state.players.length >= 2 && !state.tankSelectReady[1]) {
      let moved = false;
      if (justPressed(Keys.ArrowLeft, input)) { state.tankSelectCursor[1] = gridMove(state.tankSelectCursor[1], -1, 0, tankIds); moved = true; }
      if (justPressed(Keys.ArrowRight, input)) { state.tankSelectCursor[1] = gridMove(state.tankSelectCursor[1], 1, 0, tankIds); moved = true; }
      if (justPressed(Keys.ArrowUp, input)) { state.tankSelectCursor[1] = gridMove(state.tankSelectCursor[1], 0, -1, tankIds); moved = true; }
      if (justPressed(Keys.ArrowDown, input)) { state.tankSelectCursor[1] = gridMove(state.tankSelectCursor[1], 0, 1, tankIds); moved = true; }
      if (moved) {
        playSound('menu_nav');
        const ci = state.tankSelectCursor[1];
        p2.tankId = ci < tankIds.length ? tankIds[ci] : tankIds[0];
        p2.color = tankColor(p2.tankId);
        if (p2.tankId === 'terraformer' && !p2.terraformerMode) p2.terraformerMode = 'sapper';
      }
      // P2 Terraformer mode toggle (Shift)
      if (p2.tankId === 'terraformer' && justPressed(Keys.ShiftRight, input)) {
        p2.terraformerMode = p2.terraformerMode === 'mason' ? 'sapper' : 'mason';
      }
      if (justPressed(Keys.Enter, input) && !isTankDisabled(p2.tankId)) {
        if (!isTankUnlocked(p2.tankId)) {
          if (purchaseTank(p2.tankId)) playSound('menu_select');
        } else {
          playSound('menu_select');
          state.tankSelectReady[1] = true;
        }
      }
    }
  }

  // Both confirmed → go to stage select
  if (state.tankSelectReady[0] && state.tankSelectReady[1]) {
    state.stageSelectCursor = 0;
    state.stageSelectReady = false;
    state.randomPreviewSeed = makePreviewSeed();
    state.randomPreviewThemeId = pickRandomThemeId();
    transitionPhase(state, 'stage_select', fx);
    state.freezeUntil = state.frameCount + msToFrames(150); // input gate: outlast 67ms tapVirtualKey pulse
    return; // Don't process further input on transition frame
  }

  // Back
  if (justPressed(Keys.Escape, input)) {
    if (state.multiplayer?.enabled) {
      state.multiplayer = null;
      transitionPhase(state, 'mode_select', fx);
    } else {
      transitionPhase(state, 'mode_select', fx);
    }
  }
}
